import { useEffect } from "react";
import { ScreenOrientation } from "@capacitor/screen-orientation";

import { getGtag } from "@/lib/gtag";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "./features/auth/auth-store";
import { MobileLayout } from "./components/layout/MobileLayout";
import { ConsentBanner } from "./components/ConsentBanner";
import Welcome from "./pages/Welcome";
import Home from "./pages/Home";
import Play from "./pages/Play";
import Learn from "./pages/Learn";
import LessonPage from "./pages/LessonPage";
import { ReviewSession } from "./features/learn/components/ReviewSession";
import Minigames from "./pages/Minigames";
import Profile from "./pages/Profile";
import NotFound from "./pages/NotFound";
import LiveTable from "./pages/LiveTable";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Stats from "./pages/Stats";
import Friends from "./pages/Friends";
import Notifications from "./pages/Notifications";
import Settings from "./pages/Settings";
import Support from "./pages/Support";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Cookies from "./pages/Cookies";
import Credits from "./pages/Credits";
import HandReview from "./pages/HandReview";
import Certificate from "./pages/Certificate";
import { PreflopGame } from "./features/minigames/preflop/PreflopGame";

const queryClient = new QueryClient();

const GAPageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    const gtag = getGtag();
    if (typeof gtag === "function") {
      gtag("event", "page_view", { page_path: location.pathname + location.search });
    }
  }, [location]);
  return null;
};

const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const token = useAuthStore((s) => s.token);
  const location = useLocation();
  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
};

/** Root route: logged-in users go to home, visitors see the landing page (Welcome). Same on web and native. */
const RootRoute = () => {
  const token = useAuthStore((s) => s.token);
  return token ? <Navigate to="/home" replace /> : <Welcome />;
};

const App = () => {
  useEffect(() => {
    const lockOrientation = async () => {
      try {
        await ScreenOrientation.lock({ orientation: "portrait" });
      } catch {
        // Ignore errors in browsers that don't support it
      }
    };
    lockOrientation();
  }, []);

  return (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <GAPageTracker />
        <ConsentBanner />
        <MobileLayout>
          <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[999] focus:bg-secondary focus:text-secondary-foreground focus:px-4 focus:py-2 focus:rounded">
            Skip to content
          </a>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/home" element={<RequireAuth><Home /></RequireAuth>} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/play" element={<RequireAuth><Play /></RequireAuth>} />
            <Route path="/learn" element={<RequireAuth><Learn /></RequireAuth>} />
            <Route path="/learn/review" element={<RequireAuth><ReviewSession /></RequireAuth>} />
            <Route path="/learn/:lessonId" element={<RequireAuth><LessonPage /></RequireAuth>} />
            <Route path="/minigames" element={<RequireAuth><Minigames /></RequireAuth>} />
            <Route path="/minigames/preflop" element={<RequireAuth><PreflopGame /></RequireAuth>} />
            <Route path="/compete" element={<Navigate to="/play" replace />} />
            <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
            <Route path="/friends" element={<RequireAuth><Friends /></RequireAuth>} />
            <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />
            <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
            <Route path="/support" element={<RequireAuth><Support /></RequireAuth>} />
            <Route path="/stats" element={<RequireAuth><Stats /></RequireAuth>} />
            <Route path="/table" element={<RequireAuth><LiveTable /></RequireAuth>} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/credits" element={<Credits />} />
            <Route path="/hand-review" element={<RequireAuth><HandReview /></RequireAuth>} />
            <Route path="/certificate" element={<RequireAuth><Certificate /></RequireAuth>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </MobileLayout>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
